import { forwardRef, type TextareaHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

export const textareaVariants = cva(
  // Base
  'block w-full rounded-lg border bg-ink-50 text-ink-900 placeholder:text-ink-400 resize-y ' +
    'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-0 ' +
    'disabled:cursor-not-allowed disabled:opacity-60',
  {
    variants: {
      variant: {
        default:
          'border-ink-200 hover:border-ink-300 focus-visible:ring-accent-500/40 focus-visible:border-accent-500',
        error:
          'border-danger-500 focus-visible:ring-danger-500/40 focus-visible:border-danger-500',
      },
      size: {
        sm: 'px-2.5 py-1.5 text-sm min-h-[64px]',
        md: 'px-3 py-2 text-sm min-h-[80px]',
        lg: 'px-4 py-3 text-base min-h-[120px]',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
    },
  }
);

export interface TextareaProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'>,
    VariantProps<typeof textareaVariants> {}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, variant, size, ...props }, ref) => {
    return (
      <textarea
        ref={ref}
        className={cn(textareaVariants({ variant, size }), className)}
        {...props}
      />
    );
  }
);
Textarea.displayName = 'Textarea';
